import { PaymentPlan, legacyPricingKeys, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { BillingFrequency } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { AutopilotPlan, StripePrice } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'

export const pricePerMonth = (paymentPlan: PaymentPlan, billingFrequency: BillingFrequency) => {
  if (billingFrequency === 'year') {
    const { unit_amount: yearly_amount, amount_off: yearly_amount_off } = paymentPlan.price.year

    return (yearly_amount - (yearly_amount_off || 0)) / 12
  }

  const { unit_amount: monthly_amount, amount_off: monthly_amount_off } = paymentPlan.price.month

  return monthly_amount - (monthly_amount_off || 0)
}

export const totalPrice = (paymentPlan: PaymentPlan, billingFrequency: BillingFrequency, members_count: number) => {
  const isLegacyPlan = legacyPricingKeys.includes(paymentPlan.key)

  if (billingFrequency === 'year') {
    const { unit_amount, amount_off } = paymentPlan.price.year

    if (isLegacyPlan) return unit_amount * members_count

    return unit_amount - (amount_off || 0)
  }

  const monthly = pricePerMonth(paymentPlan, billingFrequency)

  if (isLegacyPlan) return monthly * members_count

  return monthly
}

export const formatPrice = (price?: number) => {
  if (price === null || price === undefined) return ''
  const formatted = Number(price).toFixed(2)
  return formatted.replace(/\.00$/, '')
}

const UNLIMITED_ACCOUNTS = Number.POSITIVE_INFINITY

export const findExtraAccountItem = (currentSubscription: any) =>
  currentSubscription?.subscription_items?.find(
    (item: any) => item.plan_key === PaymentPlanKey.BlazeAutopilotExtraAccounts,
  ) ?? null

/**
 * A plan's included posting-account allowance, read from its Stripe product metadata.
 * Missing or negative metadata means unlimited (Enterprise), mirroring
 * `ExternalIntegrationAccountLimits.max_limited_accounts_for_plan` on the backend.
 */
export const planAccountLimit = (plan?: Pick<AutopilotPlan, 'metadata'> | null): number => {
  const raw = plan?.metadata?.accounts
  if (raw === undefined || raw === null || raw === '') return UNLIMITED_ACCOUNTS

  const limit = Number(raw)
  if (!Number.isFinite(limit) || limit < 0) return UNLIMITED_ACCOUNTS

  return limit
}

/**
 * How many paid extra accounts the workspace would need *on `targetPlan`* — the client-side
 * mirror of `ExternalIntegration.calculate_required_additional_accounts`.
 *
 * The quantity already on the subscription cannot answer this: it has been reduced by the
 * current plan's allowance, so reusing it quotes an add-on the target plan may absorb (BLZ-16162)
 * or omit one the target plan will start charging for.
 *
 * Without a `targetPlan` this prices the subscription as it stands today.
 */
export const requiredExtraAccounts = (currentSubscription: any, targetPlan?: AutopilotPlan | null): number => {
  const currentQuantity = findExtraAccountItem(currentSubscription)?.quantity ?? 0
  if (!targetPlan) return currentQuantity

  const limit = planAccountLimit(targetPlan)
  if (limit === UNLIMITED_ACCOUNTS) return 0

  // Predates `limited_accounts_count` in the API response — quote today's charge rather than guess.
  const accountsInUse = currentSubscription?.limited_accounts_count
  if (typeof accountsInUse !== 'number') return currentQuantity

  return Math.max(0, accountsInUse - limit)
}

/**
 * The monthly rate for a single extra account. Prefers the price already on the subscription and
 * falls back to the add-on catalog, so a plan change that *introduces* the add-on can be priced.
 * Every extra-account price is the same $/month, so amortizing one interval and rescaling it is
 * equivalent to looking up the price for the target interval (which is what the backend does).
 */
const monthlyRatePerExtraAccount = (currentSubscription: any, extraAccountsAddOn?: AutopilotPlan | null): number => {
  const priceId = findExtraAccountItem(currentSubscription)?.price_id
  const subscribedPrice = currentSubscription?.prices?.find((p: StripePrice) => p.id === priceId)

  // `recurring` is null for one-time prices, which have no interval to amortize over.
  // `currentSubscription` is untyped here, so TypeScript cannot flag this read — guard explicitly.
  const price = subscribedPrice?.recurring ? subscribedPrice : extraAccountsAddOn?.prices?.find((p) => !!p.recurring)
  if (!price?.recurring) return 0

  return price.unit_amount / price.recurring.interval_count / 100
}

/**
 * Calculates the extra account charges for a given interval count
 * @param currentSubscription - The current subscription object
 * @param intervalCount - The billing interval count (1 for monthly, 3 for quarterly, etc.)
 * @param options.targetPlan - The plan being priced; omit to price the current subscription as-is
 * @param options.extraAccountsAddOn - Extra-accounts add-on catalog entry, for pricing an add-on
 *   the subscription does not carry yet
 * @returns The total extra account charges for the interval (in dollars, not cents)
 */
export const calculateExtraAccountCharges = (
  currentSubscription: any,
  intervalCount: number,
  options: { targetPlan?: AutopilotPlan | null; extraAccountsAddOn?: AutopilotPlan | null } = {},
) => {
  const quantity = requiredExtraAccounts(currentSubscription, options.targetPlan)
  if (quantity <= 0) return 0

  return monthlyRatePerExtraAccount(currentSubscription, options.extraAccountsAddOn) * quantity * intervalCount
}

/**
 * Calculates the monthly extra account charges
 * @param currentSubscription - The current subscription object
 * @param options - See `calculateExtraAccountCharges`
 * @returns The monthly extra account charges (in dollars, not cents)
 */
export const calculateMonthlyExtraAccountCharges = (
  currentSubscription: any,
  options: { targetPlan?: AutopilotPlan | null; extraAccountsAddOn?: AutopilotPlan | null } = {},
) => {
  return calculateExtraAccountCharges(currentSubscription, 1, options)
}
